import React, { useEffect, useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import AssignmentIcon from '@material-ui/icons/Assignment'
import EditIcon from '@material-ui/icons/Edit'
import CheckIcon from '@material-ui/icons/Check'
import SaveIcon from '@material-ui/icons/Save'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { Container } from '../../components'
import { vendorProjectApi } from '../../contexts'
import { VendorProject, VendorProjectFormData } from '../../models'
import { typographyStyle } from '../../theme/typographyStyle'
import { ProjectFieldsEditor } from './ProjectFieldsEditor'
import {
    ProjectFieldErrors,
    blankProjectForm,
    projectToProjectForm,
    validateProjectFields,
} from './projectFormHelpers'

interface Props {
    open: boolean
    project?: VendorProject
    onClose: () => void
}

export const VendorProjectForm = (props: Props) => {
    const { open, project, onClose } = props
    const theme = useTheme()
    const isEdit = !!project
    const { createVendorProject, updateVendorProject } = vendorProjectApi()

    const [form, setForm] = useState<VendorProjectFormData>(blankProjectForm())
    const [errors, setErrors] = useState<ProjectFieldErrors>({})
    const [submitting, setSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)

    useEffect(() => {
        if (open) {
            setForm(
                project ? projectToProjectForm(project) : blankProjectForm(),
            )
            setErrors({})
            setSubmitError(null)
        }
    }, [open, project])

    const validate = (): boolean => {
        const next = validateProjectFields(form)
        setErrors(next)
        return Object.keys(next).length === 0
    }

    const handleSubmit = async () => {
        if (!validate()) return
        setSubmitting(true)
        setSubmitError(null)
        try {
            if (isEdit && project) {
                await updateVendorProject(project.id, {
                    title: form.title,
                    description: form.description,
                    start_date: form.start_date,
                    end_date: form.end_date,
                    priority: form.priority,
                    address: form.address,
                })
            } else {
                await createVendorProject(form)
            }
            // Only close after a successful save - on failure the context API
            // throws and we land in the catch block below so the user keeps
            // their input and sees the error inline.
            onClose()
        } catch (e) {
            const msg =
                e instanceof Error && e.message
                    ? e.message
                    : 'Something went wrong. Please try again.'
            setSubmitError(msg)
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle disableTypography style={{ paddingBottom: 0 }}>
                <Container
                    alignItems="center"
                    style={{ gap: theme.spacing(1) }}
                >
                    <Container
                        alignItems="center"
                        justifyContent="center"
                        style={{
                            width: 36,
                            height: 36,
                            borderRadius: 8,
                            backgroundColor: '#e8efef',
                            color: theme.palette.brand1.main,
                        }}
                    >
                        {isEdit ? <EditIcon /> : <AssignmentIcon />}
                    </Container>
                    <Container direction="column" flex={1}>
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.h6),
                                fontWeight: 700,
                            }}
                        >
                            {isEdit ? 'Edit Project' : 'Create Project'}
                        </Container>
                        <Container
                            style={{
                                ...typographyStyle(theme.typography.caption),
                                color: theme.palette.text.secondary,
                            }}
                        >
                            {isEdit
                                ? 'Update project details'
                                : 'Set up a new project to track work'}
                        </Container>
                    </Container>
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Container>
            </DialogTitle>
            <Divider style={{ margin: theme.spacing(2, 3, 0) }} />
            <DialogContent>
                <Container
                    direction="column"
                    style={{ gap: theme.spacing(2.5) }}
                >
                    {submitError && (
                        <Container
                            alignItems="flex-start"
                            style={{
                                gap: theme.spacing(1),
                                padding: theme.spacing(1.25, 1.5),
                                borderRadius: 8,
                                border: `1px solid ${theme.palette.error.light}`,
                                backgroundColor: '#ffebee',
                                color: theme.palette.error.dark,
                            }}
                        >
                            <ErrorOutlineIcon
                                fontSize="small"
                                style={{ marginTop: 2 }}
                            />
                            <Container
                                direction="column"
                                flex={1}
                                style={{
                                    ...typographyStyle(theme.typography.body2),
                                }}
                            >
                                <span style={{ fontWeight: 700 }}>
                                    {isEdit
                                        ? 'Could not update project'
                                        : 'Could not create project'}
                                </span>
                                <span>{submitError}</span>
                            </Container>
                        </Container>
                    )}
                    <ProjectFieldsEditor
                        value={form}
                        onChange={setForm}
                        errors={errors}
                    />
                </Container>
            </DialogContent>
            <DialogActions
                style={{
                    padding: theme.spacing(1.5, 3),
                    backgroundColor: '#fafbfb',
                    borderTop: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Container flex={1} />
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={submitting}
                    style={{
                        fontWeight: 600,
                        textTransform: 'none',
                        marginRight: theme.spacing(1),
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={submitting}
                    disableElevation
                    startIcon={isEdit ? <SaveIcon /> : <CheckIcon />}
                    style={{
                        fontWeight: 700,
                        textTransform: 'none',
                        minWidth: 160,
                        boxShadow:
                            '0 1px 2px rgba(70,176,131,0.2), 0 4px 12px rgba(70,176,131,0.15)',
                    }}
                >
                    {isEdit ? 'Save Changes' : 'Create Project'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
